import {
  Typography,
  Box,
  styled,
  TextField,
  Button,
  FormHelperText,
  Backdrop,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import User_D from "../../../assets/customerAssets/user_D.jpeg";
import CameraIcon from "../../../assets/customerAssets/icons/camera.svg";
import { Formik, useFormik } from "formik";
import * as Yup from "yup";
import { supportedImageFileTypes } from "../../../helper/commonFunctions";
import { toast } from "react-toastify";
import { changePasswordApi } from "../../../api/changePassword";
import { useDispatch } from "react-redux";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { strongPasswordSchema } from "../../../utils/passwordValidation";

const CustomTextarea = styled(TextField)(() => ({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#191B2F",
    border: "1px solid #414466",
    borderRadius: "8px",
    color: "white",
    "&:hover": {
      backgroundColor: "#191B2F",
      border: "1px solid #414466",
    },
    "&.Mui-focused": {
      backgroundColor: "#191B2F",
      border: "1px solid #414466",
      outline: "none",
    },
  },

  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
    "&:hover": {
      border: "none",
    },
  },
}));

const ChangePassword = () => {
  const userData = useSelector((state) => state.user);
  const [previewImg, setPreviewImg] = useState(null);
  const profileInputRef = useRef(null);
  const editFormRef = useRef(null);
  const token = useSelector((state) => state.auth.token);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [currentPassV, setCurrentPassV] = useState(true);
  const [newPassV, setNewPassV] = useState(true);
  const [confirmPassV, setConfirmPassV] = useState(true);

  const inititialValues = {
    currentPassword: "",
    newPassword: "",
    confirm_password: "",
  };

  const validationSchema = Yup.object({
    currentPassword: Yup.string().required(
      "Please enter your current password"
    ),
    newPassword: strongPasswordSchema("Please enter your new password"),
    confirm_password: Yup.string()
      .required("Please confirm the password")
      .oneOf([Yup.ref("newPassword"), null], "Passwords must match"),
  });

  const formik = useFormik({
    initialValues: inititialValues,
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      values.user_id = userData.data.id;
      try {
        setLoading(true);
        const data = await changePasswordApi(values, token);

        if (data.success) {
          toast.success("Password updated successfully");
        } else {
          toast.error(data.message);
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
        resetForm();
      }

      // on success call this
    },
  });

  return (
    <>
      <Backdrop
        sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
        open={loading}
        // onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box className="heading">
        <Typography component="h1">CHANGE PASSWORD</Typography>
      </Box>
      <Box className="form-wrapper">
        <form
          className="edit-profile-form"
          onSubmit={formik.handleSubmit}
          ref={editFormRef}
        >
          <Box className="profile-pic custom-border-right changePassword">
            <Box sx={{ minWidth: { xs: "0px", md: "182px" } }}></Box>
          </Box>
          <Box
            className="form-field"
            sx={{
              paddingRight: {
                md: "90px !important",
                lg: "150px !important",
                xl: "290px !important",
              },
            }}
          >
            {/* first name  */}
            <Box className="form-control">
              <Typography className="label">Current Password</Typography>
              <CustomTextarea
                id="outlined-multiline-static"
                placeholder="Enter Your Current Password"
                variant="outlined"
                value={formik.values.currentPassword}
                onChange={formik.handleChange}
                name="currentPassword"
                InputProps={{
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{
                        color: "white",
                        paddingRight: "5px",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        if (currentPassV) {
                          setCurrentPassV(false);
                        } else {
                          setCurrentPassV(true);
                        }
                      }}
                    >
                      {currentPassV ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </InputAdornment>
                  ),
                  type: currentPassV ? "password" : "text",
                }}
              />

              {formik.touched.currentPassword &&
              formik.errors.currentPassword ? (
                <FormHelperText sx={{ color: "red" }}>
                  {formik.errors.currentPassword}
                </FormHelperText>
              ) : (
                ""
              )}
            </Box>

            {/* lastname  */}
            <Box className="form-control">
              <Typography className="label">New Password</Typography>
              <CustomTextarea
                id="outlined-multiline-static"
                placeholder="Please Enter New Password"
                variant="outlined"
                value={formik.values.newPassword}
                onChange={formik.handleChange}
                name="newPassword"
                InputProps={{
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{
                        color: "white",
                        paddingRight: "5px",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        if (newPassV) {
                          setNewPassV(false);
                        } else {
                          setNewPassV(true);
                        }
                      }}
                    >
                      {newPassV ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </InputAdornment>
                  ),
                  type: newPassV ? "password" : "text",
                }}
              />

              {formik.touched.newPassword && formik.errors.newPassword ? (
                <FormHelperText sx={{ color: "red" }}>
                  {formik.errors.newPassword}
                </FormHelperText>
              ) : (
                ""
              )}
            </Box>

            {/* email  */}
            <Box className="form-control">
              <Typography className="label">Re-enter New Password</Typography>
              <CustomTextarea
                id="outlined-multiline-static"
                placeholder="Please Re-enter password"
                variant="outlined"
                value={formik.values.confirm_password}
                onChange={formik.handleChange}
                name="confirm_password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      sx={{
                        color: "white",
                        paddingRight: "5px",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        if (confirmPassV) {
                          setConfirmPassV(false);
                        } else {
                          setConfirmPassV(true);
                        }
                      }}
                    >
                      {confirmPassV ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </InputAdornment>
                  ),
                  type: confirmPassV ? "password" : "text",
                }}
              />

              {formik.touched.confirm_password &&
              formik.errors.confirm_password ? (
                <FormHelperText sx={{ color: "red" }}>
                  {formik.errors.confirm_password}
                </FormHelperText>
              ) : (
                ""
              )}
            </Box>

            <Button
              className="edit-profile-btn"
              //   onClick={() => {
              //     if (editFormRef.current) {
              //       editFormRef.current.submit();
              //     }
              //   }}
              type="submit"
            >
              Save
            </Button>
          </Box>
        </form>
      </Box>
    </>
  );
};

export default ChangePassword;
